import { AbacoVuexModule, State } from "@abaco/web-common/src/app";
import { Plot } from "@abaco/portal-global-state/src/modules/global-state/models/Plot";
import {
    Activity,
    ActivityFormData,
    FormType,
    ActivityCollection,
    ActivityOrAction,
    ChartActivity,
    PlotSettings
} from "./models/Activity";
import { Fertilizer, FertilizerAction } from "./models/Fertilizer";
import { Irrigation } from "./models/Irrigation";
import { Phytochemical, PhytochemicalAction } from "./models/Phytochemical";
import { Seed, SeedAction } from "./models/Seed";
import { UnitConversion, UnitOfMeasure } from "./models/UnitsOfMeasure";
import { Action, ActionFormData } from "./models/Action";
import { WorkerData, WorkerActionData } from "./models/Worker";
import { Equipment, EquipmentSettings } from "./models/Equipment";
import { Electricity, ElectricityAction } from "./models/Electricity";
import { FreeTrack, Track } from "./models/Track";
import { Harvest, Product, ProductAction } from "./models/Product";
import { Water, WaterOut } from "./models/Water";
import { User } from "./models/Users";
import { DeliveryActionFromOut, DeliveryActionToOut } from "./models/Delivery";
import { Block } from "@abaco/crop-plan-module/src/modules/crop-plan-module/models/Block";
import { DatepickerWeather } from "./models/Weather";
import { DocumentCategory, DocumentCategoryCheck } from "./models/Document";
import { FieldRegistry, FieldRegistryType } from "./models/FieldRegistry";
import { Fuel, FuelAction } from "./models/Fuel";
export default class ActivitiesVuexModule extends AbacoVuexModule {
    @State() canViewAction!: boolean;
    @State() canEditAction!: boolean;
    @State() canEditFieldRegistry!: boolean;
    @State() canValidateExecution!: boolean;
    @State() canViewActivity!: boolean;
    @State() canEditActivity!: boolean;
    @State() canSelectWarehouse!: boolean;
    @State() canViewTracks!: boolean;
    @State() canViewSustainability!: boolean;
    @State() canEditSustainability!: boolean;
    @State() canSelectSuppliersFertilizer!: boolean;
    @State() canManageIotMaps!: boolean;
    @State() canManageEquipmentVolume!: boolean;
    @State({ default: true }) hasSimplifiedInsert!: boolean;
    @State({ default: 0 }) resetTypeSelection!: number;

    @State({ default: new Date() }) dt!: Date | null;
    @State({ default: new Date() }) detailedDefaultDate!: Date | null;

    @State({ default: 1 }) curStep!: number;

    @State() isProtocol!: boolean;

    @State() editingActivity!: Activity | null;
    @State() curActivity!: Activity | null;
    @State({ default: {} }) richActivities!: ActivityCollection;

    @State() curAction!: Action | null;
    @State() editingAction!: Action | null;

    @State() activityConfigured!: ActivityOrAction | null;

    @State() actionSelected!: boolean;

    @State({ default: [] }) selectedPlots!: Plot[];
    @State({ default: [] }) selectedBlocks!: Block[];

    @State({ default: [] }) selectedActivities!: Activity[];

    @State() formData!: ActivityFormData | null;
    @State() actionFormData!: ActionFormData | null;
    @State({ default: false }) formDataValid!: boolean;

    @State() curFormType!: FormType | null;

    @State({ default: [] }) fertilizers!: Fertilizer[];
    @State({ default: [] }) actionFertilizers!: FertilizerAction[];

    @State({ default: [] }) actionFuels!: FuelAction[];

    @State({ default: [] }) phytochemicals!: Phytochemical[];
    @State({ default: [] }) actionPhytochemicals!: PhytochemicalAction[];

    @State({ default: null }) curSeed!: Seed | null;
    @State({ default: null }) curActionSeed!: SeedAction | null;
    @State({ default: false }) validSeed!: boolean;

    @State({ default: [] }) products!: Product[];
    @State({ default: [] }) actionProductsUse!: ProductAction[];
    @State({ default: [] }) actionProductsGen!: ProductAction[];
    @State() harvest!: Harvest;

    @State() curIrrigation!: Irrigation | null;
    @State({ default: [] }) actionIrrigations!: Irrigation[];
    @State({ default: false }) validIrrigation!: boolean;

    @State() curWater!: Water | null;
    @State() curActivityWater!: WaterOut | null;
    @State() curActionWater!: Water | null;
    @State({ default: [] }) waters!: Water[];
    @State({ default: [] }) actionWaters!: Water[];
    @State({ default: false }) validWater!: boolean;

    @State({ default: [] }) actionDeliveryToProducts!: ProductAction[];
    @State({ default: [] }) actionDeliveryFromProducts!: ProductAction[];
    @State() curActionDeliveryTo!: DeliveryActionToOut | null;
    @State() curActionDeliveryFrom!: DeliveryActionFromOut | null;

    @State({ default: [] }) actionWorkers!: WorkerActionData[];
    @State({ default: [] }) workers!: WorkerData[];

    @State({ default: [] }) equipments!: Equipment[];
    @State({ default: [] }) activityEquipments!: Equipment[];

    @State({ default: [] }) electricities!: ElectricityAction[];
    @State({ default: [] }) activityElectricities!: Electricity[];

    @State({ default: {} }) unitOfMeasures!: { [family_name: string]: UnitOfMeasure[] };

    @State({ default: 0 }) triggerActionUpdate!: number;

    @State({ default: [] }) curTracks!: Track[];
    @State({ default: false }) storeHasTracks!: boolean;
    @State({ default: false }) showTracksError!: boolean;
    @State() tracksDateFrom!: Date;
    @State() tracksDateTo!: Date | null;
    @State({ default: false }) activityMapLoading!: boolean;

    @State() curWeatherWidgetData!: DatepickerWeather | null;

    @State({ default: 0 }) resetCacheWarehouse!: number;

    @State() curChartActivity!: ChartActivity;

    @State() curUser!: User | null;

    @State() freeTrackSelected!: FreeTrack | null;

    @State({ default: 0 }) triggerTrackUpdate!: number;

    @State({ default: "" }) freeTrackVkt!: string | null;

    @State({ default: 0 }) triggerReloadFreeTracks!: number;

    @State({ default: 0 }) updateCurTrack = 0;

    @State({ default: false }) isRedirectedFromWidget!: boolean;

    @State() backToModule!: string | null;
    @State() fromMainLinks!: boolean;

    @State({ default: false }) enableQualitativeAssessmentStep!: string | null;

    @State() documentCatList!: DocumentCategory[];
    @State() docForSpecificAct!: DocumentCategoryCheck[];

    @State() enableQualitativeAssFromSave!: boolean;

    @State() canViewExecutionCost!: boolean;
    @State() canEditExecutionCost!: boolean;
    @State() canViewWorker!: boolean;
    @State() canUploadActivity!: boolean;
    @State() canDownloadActivity!: boolean;
    @State() canManageScans!: boolean;
    @State() canLinkActivities!: boolean;
    @State() selectedActivityType!: ActivityOrAction;

    @State({ default: false }) addExecutionToFieldRegistry!: boolean;
    @State({ default: null }) fieldRegistryExport!: FieldRegistry | null;
    @State({ default: null }) fieldRegistryTypeExport!: FieldRegistryType | null;

    @State() storeNewAction!: Action | null;
    @State({ default: false }) inUploadExecutionMap!: boolean;

    @State({ default: null }) equipmentSettings!: EquipmentSettings | null;

    @State({ default: [] }) formTypes!: FormType[];
    @State({ default: [] }) formTypesActivityTypes!: { frmTypeId: number; activityTypes: Activity[] }[];
    @State({ default: false }) formTypesLoadingStarted!: boolean;
    @State({ default: false }) formTypesLoaded!: boolean;
    @State({ default: false }) formTypesActivityTypesLoaded!: boolean;

    @State() canEditPlannedResources!: boolean;

    @State({ default: [] }) unitConversions!: UnitConversion[];

    @State({ default: null }) plotSettings!: PlotSettings | null;
}
